import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  Card,
  Divider,
  FlexContainer,
  InputMask,
  InputText,
  Typography,
} from '@uigovpe/components';
import { PageHeader } from '@/shared/components/PageHeader';
import { Skeleton } from '@/shared/components/Skeleton';
import { ErrorNotice } from '@/shared/components/ErrorNotice';
import { LabeledValue } from '@/shared/components/LabeledValue';
import { GovBrValue } from '@/shared/components/GovBrValue';
import { ContactVerificationForm } from '@/shared/components/ContactVerificationForm';
import { useToast } from '@/shared/providers/ToastProvider';
import { messageFromError } from '@/shared/api/apiError';
import { Profile } from '@/shared/types/certificate-request';
import { brDateToIso, formatDate, isoToBrDate } from '@/shared/utils/formatting';
import { profileApi } from '@/features/certificate-request/api/profileApi';
import { contactApi } from '@/features/certificate-request/api/contactApi';


export function ProfilePage() {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [socialName, setSocialName] = useState('');
  const [motherName, setMotherName] = useState('');
  const [brBirthDate, setBrBirthDate] = useState('');
  const [personalBusy, setPersonalBusy] = useState(false);
  const [personalError, setPersonalError] = useState<string | null>(null);

  const [contactBusy, setContactBusy] = useState(false);
  const [contactError, setContactError] = useState<string | null>(null);
  const [codeRecipient, setCodeRecipient] = useState<string | null>(null);

  useEffect(() => {
    let active = true;

    profileApi
      .get()
      .then((data) => {
        if (!active) return;
        setProfile(data);
        setSocialName(data.socialName ?? '');
        setMotherName(data.motherName ?? '');
        setBrBirthDate(isoToBrDate(data.birthDate));
      })
      .catch(
        (e) => active && setLoadError(messageFromError(e, 'Não conseguimos carregar seu perfil.')),
      )
      .finally(() => active && setLoading(false));

    return () => {
      active = false;
    };
  }, []);

  const header = (
    <PageHeader
      eyebrow="Certidão Digital"
      title="Meu perfil"
      description="Dados vindos do seu login no gov.br não podem ser alterados aqui. O contato pode ser atualizado quando precisar."
      onBack={() => navigate('/')}
    />
  );

  if (loading) {
    return (
      <>
        {header}
        <Card className="cd-details__card">
          <Skeleton width="10rem" height="1.5rem" />
          <FlexContainer direction="col" gap="6">
            <Skeleton height="2.75rem" />
            <Skeleton height="2.75rem" />
            <div className="cd-grid cd-grid--2">
              <Skeleton height="2.75rem" />
              <Skeleton height="2.75rem" />
            </div>
            <Skeleton width="8rem" height="2.5rem" />
          </FlexContainer>
        </Card>
        <Card className="cd-details__card">
          <Skeleton width="6rem" height="1.5rem" />
          <FlexContainer direction="col" gap="6">
            <div className="cd-grid cd-grid--2">
              <Skeleton height="2.75rem" />
              <Skeleton height="2.75rem" />
            </div>
            <Skeleton width="10rem" height="2.5rem" />
          </FlexContainer>
        </Card>
      </>
    );
  }
  if (loadError || !profile) {
    return (
      <>
        {header}
        <ErrorNotice message={loadError ?? 'Não conseguimos carregar seu perfil.'} />
      </>
    );
  }

  const motherNameLocked = Boolean(profile.motherName);
  const birthDateLocked = Boolean(profile.birthDate);
  const birthDate = brDateToIso(brBirthDate);
  const today = new Date().toISOString().slice(0, 10);

  const motherNameError =
    !motherNameLocked && motherName.trim().length > 0 && motherName.trim().length < 3
      ? 'Informe o nome completo da sua mãe.'
      : '';
  const birthDateError =
    !birthDateLocked && brBirthDate.length > 0 && (!birthDate || birthDate >= today)
      ? 'Informe uma data de nascimento válida, no passado.'
      : '';

  const savePersonalData = async () => {
    setPersonalError(null);
    if (motherNameError || birthDateError) return;

    setPersonalBusy(true);
    try {
      const updated = await profileApi.savePersonalData({
        socialName: socialName.trim() || undefined,
        motherName: motherNameLocked ? undefined : motherName.trim() || undefined,
        birthDate: birthDateLocked ? undefined : birthDate || undefined,
      });
      setProfile(updated);
      showToast({
        severity: 'success',
        summary: 'Perfil atualizado',
        detail: 'Seus dados foram salvos.',
      });
    } catch (e) {
      setPersonalError(messageFromError(e));
    } finally {
      setPersonalBusy(false);
    }
  };

  const saveContact = async (input: Parameters<typeof profileApi.saveContact>[0]) => {
    setContactError(null);
    setContactBusy(true);
    try {
      setProfile(await profileApi.saveContact(input));
    } catch (e) {
      setContactError(messageFromError(e));
      throw e;
    } finally {
      setContactBusy(false);
    }
  };

  const sendCode = async () => {
    setContactError(null);
    setContactBusy(true);
    try {
      const result = await contactApi.sendVerificationCode();
      setCodeRecipient(result.recipient);
      return true;
    } catch (e) {
      setContactError(messageFromError(e));
      return false;
    } finally {
      setContactBusy(false);
    }
  };

  const verifyCode = async (code: string) => {
    setContactError(null);
    setContactBusy(true);
    try {
      setProfile(await contactApi.verifyCode(code));
      setCodeRecipient(null);
    } catch (e) {
      setContactError(messageFromError(e));
    } finally {
      setContactBusy(false);
    }
  };

  return (
    <>
      {header}

      <Card className="cd-details__card">
        <Typography variant="h2" size="default" fontWeight="bold">
          Dados pessoais
        </Typography>

        <ErrorNotice message={personalError} />

        <FlexContainer direction="col" gap="6">
          <LabeledValue label="CPF" value={<GovBrValue value={profile.formattedCpf} />} />
          <LabeledValue label="Nome completo" value={<GovBrValue value={profile.name} />} />

          <InputText
            inputId="profile-social-name"
            label="Nome social"
            value={socialName}
            onChange={(e) => setSocialName(e.target.value)}
            suportText="Preencha se você usa um nome diferente do nome civil."
          />

          <div className="cd-grid cd-grid--2">
            {motherNameLocked ? (
              <LabeledValue label="Nome completo da mãe" value={profile.motherName} />
            ) : (
              <InputText
                inputId="profile-mother-name"
                label="Nome completo da mãe"
                value={motherName}
                onChange={(e) => setMotherName(e.target.value)}
                className={motherNameError ? 'p-invalid' : ''}
                suportText={motherNameError}
              />
            )}
            {birthDateLocked ? (
              <LabeledValue label="Data de nascimento" value={formatDate(profile.birthDate)} />
            ) : (
              <InputMask
                id="profile-birth-date"
                label="Data de nascimento"
                mask="99/99/9999"
                placeholder="DD/MM/AAAA"
                value={brBirthDate}
                onChange={(e) => setBrBirthDate(e.value ?? '')}
                className={birthDateError ? 'p-invalid' : ''}
                suportText={birthDateError}
              />
            )}
          </div>

          <div className="cd-actions">
            <Button label="Salvar" loading={personalBusy} onClick={savePersonalData} />
          </div>
        </FlexContainer>
      </Card>

      <Card className="cd-details__card">
        <Typography variant="h2" size="default" fontWeight="bold">
          Contato
        </Typography>
        <ContactVerificationForm
          profile={profile}
          busy={contactBusy}
          error={contactError}
          codeRecipient={codeRecipient}
          onSaveContact={saveContact}
          onSendCode={sendCode}
          onVerifyCode={verifyCode}
          allowReEdit
        />
      </Card>
    </>
  );
}
